import React, { FC, ReactElement, useCallback, useState } from 'react';
import styles from './index.module.scss';

interface SliderProps {
  items: ReactElement[];
  style?: React.CSSProperties;
}

const arrow = (enabled: boolean) => {
  return (
    <svg
      className={styles.sliderArrow}
      viewBox="0 0 14 9"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 4.5H1M13 4.5L9.5 1M13 4.5L9.5 8"
        stroke={`${enabled ? '#0D9CC9' : '#D1CED8'}`}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Slider: FC<SliderProps> = ({ items, style }) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const isPrevAvailable = activeIndex > 0;
  const isNextAvailable = activeIndex < items.length - 1;

  const handlePrev = useCallback(() => {
    if (isPrevAvailable) {
      setActiveIndex(activeIndex - 1);
    }
  }, [activeIndex, isPrevAvailable]);
  const handleNext = useCallback(() => {
    if (isNextAvailable) {
      setActiveIndex(activeIndex + 1);
    }
  }, [activeIndex, isNextAvailable]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      }
      if (e.key === 'ArrowRight') {
        handleNext();
      }
    },
    [handleNext, handlePrev]
  );

  return (
    <div className={styles.slider} style={style && style}>
      <div className={styles.sliderItems}>{items[activeIndex]}</div>
      <div
        tabIndex={0}
        role="button"
        className={`${styles.sliderNav} ${styles.sliderPrev}`}
        onClick={handlePrev}
        onKeyDown={handleKeyDown}
        style={!isPrevAvailable ? { cursor: 'default' } : undefined}
      >
        {arrow(isPrevAvailable)}
      </div>
      <div
        tabIndex={0}
        role="button"
        className={`${styles.sliderNav} ${styles.sliderNext}`}
        onClick={handleNext}
        onKeyDown={handleKeyDown}
        style={!isNextAvailable ? { cursor: 'default' } : undefined}
      >
        {arrow(isNextAvailable)}
      </div>
    </div>
  );
};

export default Slider;
